import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Search, Download, Trash2, Smartphone, Cpu, Cloud } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useDeviceSocket } from "@/hooks/useDeviceLive";
import ConfirmDialog from "@/components/common/ConfirmDialog";

const PAGE = 200;
const RANGES = [["day", "Last 24 hours"], ["week", "Last 7 days"], ["month", "Last 30 days"], ["all", "All time"], ["custom", "Custom dates"]];
const SOURCES = [["", "All sources"], ["apk", "Mobile app"], ["device", "Device"], ["cloud", "Cloud"]];
const SOURCE_ICON = { apk: Smartphone, device: Cpu, cloud: Cloud };
const SOURCE_LABEL = { apk: "APK", device: "Device", cloud: "Cloud" };
const LEVEL_CLASS = { info: "border-white bg-white text-kl-black", warn: "border-amber-500 text-amber-600", error: "border-red-500 text-red-600" };
const RANGE_MS = { day: 864e5, week: 7 * 864e5, month: 30 * 864e5 };

const stamp = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" });

/** Dashboard log viewer. `admin` = super-admin (all dealers, /admin/logs); otherwise the dealer's own logs. */
export default function LogsView({ admin = false }) {
  const base = admin ? "/admin/logs" : "/logs";
  const [f, setF] = useState({ range: "day", source: "", level: "", device_id: "", tenant_id: "", q: "", start: "", end: "" });
  const [q, setQ] = useState("");            // debounced copy of f.q
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [devices, setDevices] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [clearing, setClearing] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => { const t = setTimeout(() => setQ(f.q), 300); return () => clearTimeout(t); }, [f.q]);
  useEffect(() => {
    api.get(admin ? "/admin/devices" : "/devices").then((r) => setDevices(r.data)).catch(() => {});
    if (admin) api.get("/admin/tenants").then((r) => setTenants(r.data)).catch(() => {});
  }, [admin]);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (f.range === "custom") { if (f.start) p.set("start", f.start); if (f.end) p.set("end", f.end); }
    else if (f.range !== "all") p.set("range", f.range);
    ["source", "level", "device_id"].forEach((k) => f[k] && p.set(k, f[k]));
    if (admin && f.tenant_id) p.set("tenant_id", f.tenant_id);
    if (q.trim()) p.set("q", q.trim());
    return p;
  }, [f.range, f.start, f.end, f.source, f.level, f.device_id, f.tenant_id, q, admin]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`${base}?${params}&limit=${PAGE}`);
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }, [base, params]);
  useEffect(() => { load(); }, [load]);

  const more = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`${base}?${params}&limit=${PAGE}&skip=${items.length}`);
      setItems((l) => { const seen = new Set(l.map((x) => x.id)); return [...l, ...data.items.filter((x) => !seen.has(x.id))]; });
      setTotal(data.total);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  // New entries stream in over the WebSocket; show them if they match the current filters.
  const live = useRef();
  live.current = (log) => {
    if (f.source && log.source !== f.source) return;
    if (f.level && log.level !== f.level) return;
    if (f.device_id && log.device_id !== f.device_id) return;
    if (admin && f.tenant_id && log.tenant_id !== f.tenant_id) return;
    if (f.range === "custom" && (f.end || f.start)) return;   // historical window: don't mix in new rows
    if (RANGE_MS[f.range] && Date.now() - new Date(log.ts) > RANGE_MS[f.range]) return;
    if (q.trim()) {
      const hay = `${log.message} ${log.event} ${log.serial || ""} ${log.client_name || ""}`.toLowerCase();
      if (!hay.includes(q.trim().toLowerCase())) return;
    }
    setItems((l) => (l.some((x) => x.id === log.id) ? l : [log, ...l]));
    setTotal((n) => n + 1);
  };
  useDeviceSocket(null, (msg) => { if (msg.type === "log") live.current(msg.log); }, { all: true, admin });

  const download = async () => {
    try {
      const res = await api.get(`${base}/export.csv?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `kyralink_logs_${new Date().toISOString().slice(0, 10)}.csv` });
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const clearAll = async () => {
    try {
      const { data } = await api.delete(base);
      toast.success(`Cleared ${data.deleted} log entr${data.deleted === 1 ? "y" : "ies"}`);
      setClearing(false); setItems([]); setTotal(0);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="h-full flex flex-col" data-testid="logs-page">
      <div className="px-8 pt-8 pb-4 border-b border-kl-lineLight">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="kl-label">{admin ? "Platform" : "Activity"}</p>
            <h1 className="font-display font-light text-3xl mt-1">Logs <span className="font-mono text-sm text-kl-mutedLight" data-testid="logs-total">{total}</span></h1>
          </div>
          <div className="flex gap-2">
            <button onClick={download} className="kl-btn h-9 px-3 text-xs gap-2 border border-white bg-transparent text-white" data-testid="logs-download"><Download size={14} /> Download CSV</button>
            <button onClick={() => setClearing(true)} className="kl-btn-outline h-9 px-3 text-xs gap-2 text-red-600 border-red-300" data-testid="logs-clear"><Trash2 size={14} /> Clear logs</button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-kl-mutedLight" />
            <input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search message, event, serial, client" className="kl-input-box pl-9 h-9 w-full" data-testid="logs-search" />
          </div>
          <select value={f.range} onChange={(e) => set("range", e.target.value)} className="kl-input-box h-9 w-40" data-testid="logs-range">
            {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {f.range === "custom" && <>
            <input type="date" value={f.start} max={f.end || undefined} onChange={(e) => set("start", e.target.value)} className="kl-input-box h-9" data-testid="logs-start" />
            <span className="text-kl-mutedLight text-xs">to</span>
            <input type="date" value={f.end} min={f.start || undefined} onChange={(e) => set("end", e.target.value)} className="kl-input-box h-9" data-testid="logs-end" />
          </>}
          <select value={f.source} onChange={(e) => set("source", e.target.value)} className="kl-input-box h-9 w-36" data-testid="logs-source">
            {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={f.level} onChange={(e) => set("level", e.target.value)} className="kl-input-box h-9 w-32" data-testid="logs-level">
            <option value="">All levels</option><option value="info">Info</option><option value="warn">Warning</option><option value="error">Error</option>
          </select>
          {admin && (
            <select value={f.tenant_id} onChange={(e) => set("tenant_id", e.target.value)} className="kl-input-box h-9 w-40" data-testid="logs-tenant">
              <option value="">All dealers</option>{tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <select value={f.device_id} onChange={(e) => set("device_id", e.target.value)} className="kl-input-box h-9 w-44" data-testid="logs-device">
            <option value="">All devices</option>{devices.map((d) => <option key={d.id} value={d.id}>{d.serial}{d.client_name && d.client_name !== d.serial ? ` · ${d.client_name}` : ""}</option>)}
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto kl-scroll-light px-8 pt-6 pb-10">
        <div className="border border-kl-lineLight">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-kl-light border-b border-kl-lineLight">
            <tr className="kl-label text-left">
              <th className="px-4 py-2.5 w-44">Time</th><th className="px-3 py-2.5 w-24">Source</th><th className="px-3 py-2.5 w-20">Level</th>
              <th className="px-3 py-2.5 w-40">Device</th><th className="px-4 py-2.5">Message</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-kl-mutedLight" data-testid="logs-empty">{busy ? "Loading…" : "No log entries match these filters."}</td></tr>}
            {items.map((l) => {
              const Icon = SOURCE_ICON[l.source] || Cloud;
              return (
                <tr key={l.id} className="border-b border-kl-lineLight last:border-b-0 align-top" data-testid={`log-row-${l.id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">{stamp(l.ts)}</td>
                  <td className="px-3 py-2.5"><span className="inline-flex items-center gap-1.5 text-xs"><Icon size={13} />{SOURCE_LABEL[l.source]}</span></td>
                  <td className="px-3 py-2.5"><span className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${LEVEL_CLASS[l.level]}`}>{l.level}</span></td>
                  <td className="px-3 py-2.5 font-mono text-xs">{l.serial || "—"}</td>
                  <td className="px-4 py-2.5"><span className="font-mono text-[10px] text-kl-mutedLight mr-2">{l.event}</span>{l.message}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {items.length < total && (
          <div className="pt-5 text-center"><button onClick={more} disabled={busy} className="kl-btn-outline h-9 px-4 text-xs" data-testid="logs-more">Load more ({total - items.length} older)</button></div>
        )}
      </div>
      <ConfirmDialog open={clearing} onOpenChange={setClearing} title="Clear all logs?"
        description={admin ? "This permanently deletes every log entry for all dealers, not just the ones matching your filters. It cannot be undone." : "This permanently deletes every log entry for your account, not just the ones matching your filters. It cannot be undone."}
        confirmLabel="Clear all logs" onConfirm={clearAll} />
    </div>
  );
}
